import { useAdvanceTableContext } from 'providers/AdvanceTableProvider';
import { Table, TableProps } from 'react-bootstrap';
import { flexRender } from '@tanstack/react-table';
import { memo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSort,
  faSortDown,
  faSortUp
} from '@fortawesome/free-solid-svg-icons';

interface RowData {
  _id?: string;
  id?: string;
}

interface AdvanceTableProps {
  headerClassName?: string;
  bodyClassName?: string;
  rowClassName?: string;
  tableProps?: TableProps;
  hasFooter?: boolean;
}

const AdvanceTable = memo(
  ({
    headerClassName,
    bodyClassName,
    rowClassName,
    tableProps,
    hasFooter
  }: AdvanceTableProps) => {
    const table = useAdvanceTableContext<RowData>();

    const handleSort = useCallback(
      (handler: ((event: unknown) => void) | undefined) => {
        return (e: React.MouseEvent) => {
          e.preventDefault();
          handler?.(e);
        };
      },
      []
    );

    if (!table) return null;

    const rows = table.getRowModel().rows;
    const hasData = rows.length > 0;

    return (
      <div className="scrollbar ms-n1 ps-1">
        <Table {...tableProps} style={{ marginBottom: '73px !important' }}>
          <thead className={headerClassName}>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        className={
                          header.column.getCanSort()
                            ? 'cursor-pointer select-none'
                            : ''
                        }
                        onClick={handleSort(
                          header.column.getToggleSortingHandler()
                        )}
                      >
                        <div className="d-flex align-items-center gap-2">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {header.column.getCanSort() && (
                            <span className="sort-icon">
                              {{
                                asc: (
                                  <FontAwesomeIcon
                                    icon={faSortUp}
                                    className="fs--2"
                                  />
                                ),
                                desc: (
                                  <FontAwesomeIcon
                                    icon={faSortDown}
                                    className="fs--2"
                                  />
                                ),
                                false: (
                                  <FontAwesomeIcon
                                    icon={faSort}
                                    className="fs--2"
                                  />
                                )
                              }[header.column.getIsSorted() as string] ?? null}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className={bodyClassName}>
            {hasData ? (
              rows.map(row => (
                <tr key={row?.original?._id || row.id} className={rowClassName}>
                  {row.getVisibleCells().map(cell => (
                    <td
                      key={`${row?.original?._id || row.id}_${cell.column.id}`}
                      {...cell.column.columnDef.meta?.cellProps}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={table.getAllColumns().length}
                  className="text-center py-4"
                >
                  No data found
                </td>
              </tr>
            )}
          </tbody>
          {hasFooter && (
            <tfoot>
              {table.getFooterGroups().map(footerGroup => (
                <tr key={footerGroup.id} className="border-0">
                  {footerGroup.headers.map(header => (
                    <th
                      key={header.id}
                      {...header.column.columnDef.meta?.footerProps}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.footer,
                            header.getContext()
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </tfoot>
          )}
        </Table>
      </div>
    );
  }
);

AdvanceTable.displayName = 'AdvanceTable';

export default AdvanceTable;
